import { crmGenerated, ContactTask } from './generated/crm';


export const crm = crmGenerated.enhanceEndpoints({
  addTagTypes: ['Contact', 'Organization'],
  endpoints: {
    appCrmContactFieldChoicesCreate(endpoint) {
      endpoint.invalidatesTags = (res, err, arg) => [{ type: 'ContactFieldTemplate', id: arg.contact_field_template }];
    },
    appCrmCompaniesPartialUpdate: (endpoint) => {
      if (endpoint.query) {
        endpoint.query = (arg) => {
          // when logo is being updated we want to send multipart form-data instead of JSON
          if (arg.patchedCompany.logo) {
            const formData = new FormData();
            formData.append('logo', arg.patchedCompany.logo);
            return {
              url: `/api/v1_15/app-crm/companies/${arg.id}/`,
              method: 'PATCH',
              body: formData,
            };
          }

          return {
            url: `/api/v1_15/app-crm/companies/${arg.id}/`,
            method: 'PATCH',
            body: arg.patchedCompany,
          };
        };
      }
    },
    appCrmContactsPartialUpdate: (endpoint) => {
      if (endpoint.query) {
        endpoint.query = (arg) => {
          // when avatar is being updated we want to send multipart form-data instead of JSON
          if (arg.patchedContact.avatar) {
            const formData = new FormData();
            formData.append('avatar', arg.patchedContact.avatar);
            return {
              url: `/api/v1_15/app-crm/contacts/${arg.id}/`,
              method: 'PATCH',
              body: formData,
            };
          }

          return {
            url: `/api/v1_15/app-crm/contacts/${arg.id}/`,
            method: 'PATCH',
            body: arg.patchedContact,
          };
        };
      }
    },
    appCrmContactTasksPartialUpdate: (endpoint) => {
      // Add optimistic updates for task status changes
      endpoint.onQueryStarted = async (arg, { dispatch, queryFulfilled, getState }) => {
        // Only apply optimistic updates for status changes
        if (!arg.patchedContactTask?.status) {
          return;
        }

        const taskId = arg.id;
        const newStatus = arg.patchedContactTask.status;
        
        // Update all ContactTasksList queries optimistically
        const patchResults = updateQueryDataMulti(
          'appCrmContactTasksList',
          undefined, // No partial args - update all ContactTasksList queries
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (draft: any) => {
            if (draft?.data) {
              const taskIndex = draft.data.findIndex((task: ContactTask) => task.id === taskId);
              if (taskIndex !== -1) {
                draft.data[taskIndex] = { ...draft.data[taskIndex], status: newStatus };
              }
            }
          },
          dispatch,
          getState
        );
        
        try {
          await queryFulfilled;
        } catch (error) {
          // Revert all optimistic updates on error
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          patchResults.forEach((patchResult: any) => patchResult.undo());
        }
      };
    },
  },
  // TODO: cache normalization using tags
  // endpoints: Object.fromEntries(
  //   Object.entries(crmGenerated.endpoints).map(([key, endpoint]) => {
  //     // eslint-disable-next-line @typescript-eslint/no-explicit-any
  //     const enhancedEndpoint = (ept: EndpointDefinition<unknown, () => any, string, unknown>) => {
  //       if (key.split('apiLatestAppCrmContactsJson')[1] === 'Create') {

  //       }
  //       // ept.transformResponse = (res) => {
  //       //   // check the tags and the operation (from the end of the key) to determine what entity adapter
  //       //   // method to call (create, update, etc.)
  //       //   // return {}
  //       // }
  //       // we don't want to keep the default generated tags right now
  //       if (ept.providesTags?.length) ept.providesTags = [];
  //       if (ept.invalidatesTags?.length) ept.invalidatesTags = [];
  //       // ept.serializeQueryArgs = ({ queryArgs, endpointDefinition, endpointName }) => {
  //       //   return 'newCacheKey';
  //       // };
  //     };
  //     return [key, enhancedEndpoint];
  //   })
  // ),
  // endpoints: {
  //   apiLatestAppCoreLeadersList(endpoint) {
  //     // endpoint.providesTags = ['Leader'];
  //     // endpoint.invalidatesTags = (result, err, arg) => [{ type: 'Leader', id: arg.id }];
  //     // endpoint.keepUnusedDataFor = 120;
  //     endpoint.transformResponse = () => {
  //       // transform response here
  //       return { results: [] };
  //     };
  //   },
  // },
});

/**
 * Utility function to update query data for multiple queries with partial argument matching
 * @param endpointName - The name of the endpoint (e.g., 'appCrmContactTasksList')
 * @param partialArgs - Partial query arguments to match against cached queries
 * @param updateFn - Function to update the draft data
 * @param dispatch - RTK Query dispatch function
 * @param getState - RTK Query getState function
 * @returns Array of patch results that can be undone
 */
export const updateQueryDataMulti = (
  endpointName: string,
  partialArgs: Record<string, unknown> | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateFn: (draft: any) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getState: () => any
) => {
  const state = getState();
  const api = (state.api as Record<string, unknown>) || {};
  const queries = (api.queries as Record<string, unknown>) || {};
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const patchResults: any[] = [];
  
  // Find all queries that match the endpoint name and partial arguments
  for (const [queryKey] of Object.entries(queries)) {
    if (queryKey.startsWith(`${endpointName}(`)) {
      try {
        // Extract query args from the key
        const argsString = queryKey.slice(`${endpointName}(`.length, -1);
        const queryArgs = JSON.parse(argsString);
        
        // Check if this query matches the partial arguments
        let matches = true;
        if (partialArgs) {
          for (const [key, value] of Object.entries(partialArgs)) {
            if (queryArgs[key] !== value) {
              matches = false;
              break;
            }
          }
        }
        
        if (matches) {
          const patchResult = dispatch(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            crm.util.updateQueryData(endpointName as any, queryArgs, updateFn)
          );
          patchResults.push(patchResult);
        }
      } catch (e) {
        // Skip invalid query keys
        continue;
      }
    }
  }
  
  return patchResults;
};
